import { Injectable } from '@angular/core';
import { DyspachQuoteResponse, DyspachQuoteRequest, DyspachGoods } from './dyspach-api.service';

@Injectable({
  providedIn: 'root'
})
export class DyspachMapperService {
    constructor() { }

    /**
     * Map a Dyspach quote response to our system's format
     * @param dyspachResponse The response from Dyspach API
     * @param serviceOption The selected service option (economy, express, etc.)
     * @returns Quote data in our system's format
     */
    mapFromQuoteResponse(dyspachResponse: DyspachQuoteResponse, serviceOption?: string): any {
        // Find the selected rate
        let selectedRate = null;

        if (serviceOption && dyspachResponse.quote.length > 0) {
            selectedRate = dyspachResponse.quote.find(rate => 
                rate.serviceClass.toLowerCase() === serviceOption.toLowerCase()
            );
        }
        
        // If no matching service option is found, use the first quote
        if (!selectedRate && dyspachResponse.quote.length > 0) {
            selectedRate = dyspachResponse.quote[0];
        }

        // Calculate price excluding GST
        const totalAmount = parseFloat(selectedRate?.totalAmount || 0);
        const gstAmount = parseFloat(selectedRate?.gstAmount || 0);
        const priceExGst = totalAmount - gstAmount;

        // Map packagings
        const packagings = dyspachResponse.goods.map((good, index) => ({
        id: index + 1,
        outside_quote_reference: selectedRate.quoteId || null,
        packaging_type_id: this.getPackagingTypeId(good.goodsType),
        packaging_type_name: good.goodsType,
        quantity: good.goodsQuantity,
        length: good.goodsLength,
        width: good.goodsWidth,
        height: good.goodsHeight,
        weight: good.goodsItemWeight,
        total_weight: good.goodsItemWeight * good.goodsQuantity,
        chargeablePallets: this.calculateChargeablePallets(good),
        created_by: 1,
        updated_by: null
        }));

        // Calculate total weight
        const totalWeight = packagings.reduce((sum, pkg) => sum + pkg.total_weight, 0);
        
        // Calculate chargeable pallets
        const chargeablePallets = Math.ceil(
            packagings.reduce((sum, pkg) => sum + pkg.chargeablePallets, 0)
        );

        // Map service class to our system's options (only 'express' or 'economy')
        const mappedServiceOption = this.mapServiceClass(selectedRate?.serviceClass || '');
        console.log("selectedRate?.serviceClass.toLowerCase()", selectedRate?.serviceClass.toLowerCase())
        console.log("mappedServiceOption", mappedServiceOption)
        console.log("mappedServiceOption 2", selectedRate?.amount)

        return {
            // Quote details
            outside_reference_no: selectedRate?.quoteId || null,
            service_option: this.getServiceOptionId(mappedServiceOption),
            est_total_price: priceExGst || 0,
            
            // Pickup details
            pickup_suburb: dyspachResponse.sender.shipperSuburbName  || '',
            pickup_state: dyspachResponse.sender.shipperStateName || '',
            pickup_postcode: dyspachResponse.sender.shipperPostalCode || '',
            pickup_full_suburb: `${dyspachResponse.sender.shipperSuburbName || ''}, ${dyspachResponse.sender.shipperStateName || ''} ${dyspachResponse.sender.shipperPostalCode || ''}`,
            
            // Delivery details
            delivery_suburb: dyspachResponse.receiver.receiverSuburbName || '',
            delivery_state: dyspachResponse.receiver.receiverStateName || '',
            delivery_postcode: dyspachResponse.receiver.receiverPostalCode || '',
            delivery_full_suburb: `${dyspachResponse.receiver.receiverSuburbName || ''}, ${dyspachResponse.receiver.receiverStateName || ''} ${dyspachResponse.receiver.receiverPostalCode || ''}`,
            
            // Package details
            packagings: packagings,
            total_weight: totalWeight,
            chargeable_pallets: chargeablePallets,
            
            // Other details
            is_dg: false,
            is_hand_unload: false,
            is_trailer_split: false,
            has_tailgate: false,
            
            // For reference, store the original Dyspach data
            dyspach_data: dyspachResponse
        };
    }

    /**
     * Calculate chargeable pallets based on dimensions and weight.
     * Formula must match getBookingPallets() in BookingComponent:
     *   physicalWeight = goodsItemWeight × qty
     *   volume_m3      = qty × (l/100) × (w/100) × (h/100)   ← qty applied once
     *   cubicWeight    = volume_m3 × 333
     *   chargeWeight   = max(physicalWeight, cubicWeight)
     *   chargeablePallets = chargeWeight / 1000               ← no extra qty factor
     *
     * @param good The goods item
     * @returns Number of chargeable pallets
     */
    private calculateChargeablePallets(good: DyspachGoods): number {
        const qty = good.goodsQuantity || 1;

        // Physical weight for the whole line
        const physicalWeight = good.goodsItemWeight * qty;

        // Volumetric weight for the whole line (dimensions in cm → m)
        const volume_m3 = qty * (good.goodsLength / 100) * (good.goodsWidth / 100) * (good.goodsHeight / 100);
        const cubicWeight = volume_m3 * 333;

        // Chargeable weight is the greater of the two
        const chargeWeight = Math.max(physicalWeight, cubicWeight);

        // 1 pallet = 1000 kg
        return chargeWeight / 1000;
    }

    /**
     * Map goods type to packaging type ID in our system
     * @param goodsType The type of goods from Dyspach
     * @returns The corresponding packaging type ID in our system
     */
    private getPackagingTypeId(goodsType: string): number {
        switch (goodsType.toLowerCase()) {
            case 'pallet':
                return 10;                  
            case 'carton':
                return 17;                 
            case 'skid':
                return 18;                  
            case 'oversized plain pallet':
                return 19;                  
            case 'chep pallet':
                return 21;                  
            case 'oversized chep pallet':
                return 22;                  
            case 'loscam pallet':
                return 23;                  
            case 'oversized loscam pallet':
                return 24;                  
            default:
                return 10;                  // Default to pallet
        }
    }

    /**
     * Map Dyspach service class to our system's service options
     * @param serviceClass The service class from Dyspach
     * @returns 'express' if service class contains 'express', otherwise 'economy'
     */
    private mapServiceClass(serviceClass: string): string {
        if (serviceClass.toLowerCase().includes('express')) {
            return 'express';
        }
        return 'economy';
    }

    /**
     * Convert service alias to numeric ID
     * @param serviceAlias The service alias from Dyspach (e.g., "STANDARD", "EXPRESS")
     * @returns Numeric service option ID
     */
    private getServiceOptionId(serviceAlias: string): number {
        if (!serviceAlias) return 1; // Default to economy
        
        switch (serviceAlias.toLowerCase()) {
            case 'standard':
            case 'economy':
            return 1;
            case 'express':
            case 'express_pallets':
            case 'crl_express':
            return 2;
            default:
            console.log('Unknown service alias:', serviceAlias, 'defaulting to economy (1)');
            return 1; // Default to economy
        }
    }
}